import React, { useState, useMemo } from 'react';
import { PalletBox, PalletBoxStatus } from '../../../models/pallet-box';
import FontAwesomeTile from '../../utils/FontAwesomeTile';
import { faBoxOpen, faBox } from '@fortawesome/free-solid-svg-icons';
import PalletBoxFlag from '../PalletBoxFlag';
import { Button } from '@cimpress/react-components';
import { useMeasure } from 'react-use';
import { useFeature } from '../../../store/hooks/useFeature';
import { Features } from '../../../models/features';
import momentTimezone from 'moment-timezone';
import { useCurrentLocation } from '../../../store/hooks/useCurrentLocation';
import { Location } from '../../../models/location';

// tslint:disable-next-line:import-name
import style from './index.module.css';
import { useModal } from '../../../contexts/modal-context';
import { PalletBoxShipmentsModal } from '../PalletBoxShipmentsModal';
import { palletBoxName } from '../PalletBoxName';

export interface PalletBoxListProps {
  palletBoxes: PalletBox[];
  className?: string;
  highlight?: string;
}

function palletBoxSort(useCutoffFeature: boolean, location: Location) {
  let cutoffMoment: momentTimezone.Moment;
  if (useCutoffFeature) {
    const now = momentTimezone.tz(location.localeSettings.timezone);
    cutoffMoment = momentTimezone.tz(`${now.format('YYYY-MM-DD')} 15:00`, location.localeSettings.timezone);
    if (now.isBefore(cutoffMoment)) {
      cutoffMoment = cutoffMoment.subtract(momentTimezone.duration(1, 'days'));
    }
  } else {
    const now = momentTimezone.tz();
    cutoffMoment = now.subtract(momentTimezone.duration(1, 'days'));
  }
  const isFlagged = (pb: PalletBox) => cutoffMoment.isBefore(pb.createdAt);

  return (palletBoxA: PalletBox, palletBoxB: PalletBox) => {
    if (isFlagged(palletBoxA) !== isFlagged(palletBoxB)) {
      return isFlagged(palletBoxA) ? 1 : -1;
    }
    if (palletBoxA.status === palletBoxB.status) {
      return palletBoxA.externalId.localeCompare(palletBoxB.externalId);
    }
    return palletBoxA.status === PalletBoxStatus.OPEN ? -1 : 1;
  };
}

// tslint:disable-next-line:function-name
export default function PalletBoxList({
  palletBoxes,
  className,
  highlight,
}: PalletBoxListProps) {
  const modal = useModal();
  const { value: useCutoffFeature } = useFeature(Features.EXPERIMENTAL_PB_FLAG);
  const location = useCurrentLocation();
  const [ref, { width }] = useMeasure();
  const [page, setPage] = useState(0);
  const pageSize = useMemo(
    () => width / 120,
    [width],
  );

  return <div
    className={`${style.PalletBoxes} ${className || ''}`}
    ref={ref}
  >
    {palletBoxes
      .sort(palletBoxSort(useCutoffFeature, location))
      .slice(0, (page + 1) * pageSize)
      .map((palletBox, i) =>
        <FontAwesomeTile
          key={i}
          className={style.PalletBoxTile}
          icon={palletBox.status === PalletBoxStatus.OPEN ? faBoxOpen : faBox}
          topRightLabel={`${palletBox.shipmentCount}`}
          bottomRightLabel={<PalletBoxFlag palletBox={palletBox}/>}
          label={palletBoxName(palletBox)}
          color={`var(--${palletBox.status === PalletBoxStatus.OPEN ? 'ocean' : 'success'}`}
          highlight={highlight}
          onClick={() => modal(
            palletBoxName(palletBox),
            () => <PalletBoxShipmentsModal palletBox={palletBox}/>,
            {
              bsStyle: 'info',
              bsSize: 'lg',
              style: {
                width: '80%',
              },
            },
          )}
        />,
      )}
    {(page + 1) * pageSize < palletBoxes.length &&
      <Button onClick={() => setPage(page + 1)} type="link" className={style.ShowMore}>
        Show more
      </Button>
    }
  </div>;
}
